import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, ShieldCheck, Check, UserCheck } from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import { useUpdateSeniorFlowProfile } from 'src/hooks/useSeniorFlow';
import {
  REFERENCE_DATA_QUERY_OPTIONS,
  useReferenceData,
  useReferenceDataPublic,
  useResumeProfile,
} from 'src/hooks/useResumeData';
import { trackEvent } from 'src/utils/analytics';
import { buildProfilePayload } from './seniorFlowFields';

const NOTICE = [
  { value: 0, label: 'Immediate' },
  { value: 15, label: '15 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
];

// Fallback options for the no-token dev preview only — real options come from
// the reference-data API (`preferedLocations`, each { id, name }).
const DEMO_LOCATIONS = [
  { id: 1, name: 'Bengaluru' },
  { id: 2, name: 'Hyderabad' },
  { id: 3, name: 'Pune' },
  { id: 4, name: 'Mumbai' },
  { id: 5, name: 'Delhi NCR' },
  { id: 6, name: 'Chennai' },
  { id: 7, name: 'Remote' },
];

const toNum = (s) => {
  const n = parseFloat(String(s || '').replace(/[^0-9.]/g, ''));
  return Number.isNaN(n) ? null : n;
};

export function DetailsStage({
  scope,
  firstName,
  companyName,
  demo,
  onSubmitted,
  onBack,
}) {
  const { toast } = useToast();
  const isProtected = !!scope.secMappingId;
  const token = scope.token || null;
  const updateProfile = useUpdateSeniorFlowProfile(scope);

  // Preferred-location options come from the reference-data API — the public
  // (token) one on a magic link, the authenticated one when opened from "My
  // Applications". Both hooks are always called (rules of hooks); only the
  // one matching `scope` is actually enabled. The form stores + submits
  // location IDs (no scope at all, in dev → demo options).
  const publicRefQuery = useReferenceDataPublic(isProtected ? null : token);
  const protectedRefQuery = useReferenceData(REFERENCE_DATA_QUERY_OPTIONS, {
    enabled: isProtected,
  });
  const refData = isProtected ? protectedRefQuery.data : publicRefQuery.data;
  const refLoading = isProtected
    ? protectedRefQuery.isLoading
    : publicRefQuery.isLoading;
  const locationOptions = refData?.preferedLocations?.length
    ? refData.preferedLocations
    : (!token && !isProtected && DEMO_LOCATIONS) || [];

  // Pre-fill from the candidate's existing profile — public (token) mode
  // only for now; protected mode skips this convenience (the candidate's
  // master profile lives at /profile-dashboard). Silently no-ops without a
  // token, or if the candidate has no profile on file yet (useResumeProfile's
  // query just stays empty rather than surfacing here).
  const { data: resumeData } = useResumeProfile(token);
  const hasPrefilled = useRef(false);
  const [prefilled, setPrefilled] = useState(false);

  const [form, setForm] = useState({
    currentCtc: '',
    expectedCtc: '',
    notice: null,
    locations: [],
    relocate: false,
    phone: '',
    note: '',
  });
  const [invalid, setInvalid] = useState({});
  const [missing, setMissing] = useState([]);

  useEffect(() => {
    if (hasPrefilled.current || !resumeData) return;
    hasPrefilled.current = true;

    const patch = {};
    if (resumeData.currentCTC)
      patch.currentCtc = String(Number(resumeData.currentCTC) / 100000);
    if (resumeData.expectedCTC)
      patch.expectedCtc = String(Number(resumeData.expectedCTC) / 100000);
    if (resumeData.noticePeriod != null && resumeData.noticePeriod !== '')
      patch.notice = Number(resumeData.noticePeriod);
    if (resumeData.preferredLocations?.length)
      patch.locations = resumeData.preferredLocations.map(Number);
    if (resumeData.openToRelocation != null)
      patch.relocate = resumeData.openToRelocation;
    if (resumeData.contact) patch.phone = resumeData.contact;

    if (Object.keys(patch).length) {
      setForm((prev) => ({ ...prev, ...patch }));
      setPrefilled(true);
    }
  }, [resumeData]);

  const refs = {
    currentCtc: useRef(null),
    expectedCtc: useRef(null),
    notice: useRef(null),
    locations: useRef(null),
  };
  const bannerRef = useRef(null);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const clearInvalid = (key) =>
    setInvalid((prev) => ({ ...prev, [key]: false }));

  const toggleLocation = (loc) =>
    setForm((prev) => ({
      ...prev,
      locations: prev.locations.includes(loc)
        ? prev.locations.filter((l) => l !== loc)
        : [...prev.locations, loc],
    }));

  const readValues = () => ({
    currentCtc: toNum(form.currentCtc),
    expectedCtc: toNum(form.expectedCtc),
    notice: form.notice,
    locations: form.locations,
    relocate: form.relocate,
    phone: form.phone.trim(),
    note: form.note.trim(),
  });

  const validate = (v) => {
    const miss = [];
    const inv = {};
    if (v.currentCtc === null || v.currentCtc <= 0) {
      inv.currentCtc = true;
      miss.push(['Add your current fixed CTC', 'currentCtc']);
    }
    if (v.expectedCtc === null || v.expectedCtc <= 0) {
      inv.expectedCtc = true;
      miss.push(['Add your expected fixed CTC', 'expectedCtc']);
    }
    if (v.notice === null) {
      inv.notice = true;
      miss.push(['Pick your notice period', 'notice']);
    }
    if (!v.locations.length) {
      inv.locations = true;
      miss.push(['Pick at least one location', 'locations']);
    }
    setInvalid(inv);
    setMissing(miss);
    return miss.length === 0;
  };

  const jumpTo = (key) => {
    const el = refs[key]?.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable = el.matches('input') ? el : el.querySelector('input');
    if (focusable) focusable.focus();
  };

  const handleSubmit = async () => {
    const v = readValues();
    if (!validate(v)) {
      bannerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }
    trackEvent('form_submit', 'Senior Flow', 'details');
    const locationNames = v.locations
      .map((id) => locationOptions.find((loc) => loc.id === id)?.name)
      .filter(Boolean);

    // No token/secMappingId to PATCH against in the local demo preview —
    // skip the API call and go straight to the next stage.
    if (demo) {
      onSubmitted({ ...v, locations: locationNames });
      return;
    }

    try {
      // One PATCH for the whole form: key `interview_profile_update`, all
      // fields flattened into `data`. The API wants location IDs (`v` as-is);
      // the Thanks screen wants names, so resolve those just for display.
      await updateProfile.mutateAsync(buildProfilePayload(v));
      trackEvent('form_success', 'Senior Flow', 'details');
      onSubmitted({ ...v, locations: locationNames });
    } catch {
      trackEvent('form_error', 'Senior Flow', 'details');
      toast({
        message: "We couldn't send that just now. Please try again.",
        variant: 'destructive',
      });
    }
  };

  return (
    <section className="slf-stage-in" aria-labelledby="slf-detailsHead">
      <p className="slf-eyebrow">Step 2 of 2 · about a minute</p>
      <h2 id="slf-detailsHead" style={{ marginTop: 10 }}>
        Four quick things, {firstName}
      </h2>
      <p className="slf-read" style={{ marginTop: 8 }}>
        These let us check the role actually works for you before either of us
        spends time on a call.
      </p>

      {missing.length > 0 && (
        <div
          className="slf-banner slf-warn"
          role="alert"
          ref={bannerRef}
          style={{ marginTop: 16 }}
        >
          <AlertTriangle className="slf-ic" aria-hidden="true" />
          <div style={{ minWidth: 0 }}>
            <strong>
              {missing.length === 1
                ? '1 thing needs your input'
                : `${missing.length} things need your input`}
            </strong>
            <ul className="slf-vlist">
              {missing.map(([label, target]) => (
                <li key={target}>
                  <button type="button" onClick={() => jumpTo(target)}>
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {prefilled && (
        <div className="slf-banner slf-info" style={{ marginTop: 16 }}>
          <UserCheck className="slf-ic" aria-hidden="true" />
          <div>
            We&apos;ve filled this in from your profile — update anything
            that&apos;s changed.
          </div>
        </div>
      )}

      <hr className="slf-rule" style={{ margin: '20px 0' }} />

      {/* CTC */}
      <div className="slf-field">
        <span className="slf-field-label">Current fixed CTC</span>
        <span className="slf-field-hint">
          Fixed pay only — leave out variable and ESOPs.
        </span>
        <div
          className="slf-input-group"
          data-invalid={invalid.currentCtc ? 'true' : 'false'}
        >
          <span className="slf-pfx" aria-hidden="true">
            ₹
          </span>
          <input
            ref={refs.currentCtc}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="32"
            aria-label="Current fixed CTC in lakhs per annum"
            value={form.currentCtc}
            onChange={(e) => {
              set({ currentCtc: e.target.value });
              clearInvalid('currentCtc');
            }}
          />
          <span className="slf-sfx" aria-hidden="true">
            LPA
          </span>
        </div>
      </div>

      <div className="slf-field">
        <span className="slf-field-label">Expected fixed CTC</span>
        <span className="slf-field-hint">
          A ballpark is fine. It tells us early if the budget won&apos;t work.
        </span>
        <div
          className="slf-input-group"
          data-invalid={invalid.expectedCtc ? 'true' : 'false'}
        >
          <span className="slf-pfx" aria-hidden="true">
            ₹
          </span>
          <input
            ref={refs.expectedCtc}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="45"
            aria-label="Expected fixed CTC in lakhs per annum"
            value={form.expectedCtc}
            onChange={(e) => {
              set({ expectedCtc: e.target.value });
              clearInvalid('expectedCtc');
            }}
          />
          <span className="slf-sfx" aria-hidden="true">
            LPA
          </span>
        </div>
      </div>

      <div className="slf-banner slf-info" style={{ marginTop: 12 }}>
        <ShieldCheck className="slf-ic" aria-hidden="true" />
        <div>
          Pay details stay masked inside AHEDGE. A recruiter has to deliberately
          reveal them, and every reveal is logged against their account.
        </div>
      </div>

      {/* Notice */}
      <div className="slf-field" style={{ marginTop: 20 }} ref={refs.notice}>
        <span className="slf-field-label" id="slf-lblNotice">
          Notice period
        </span>
        <span className="slf-field-hint">How soon you could start.</span>
        <div
          className="slf-chips"
          role="radiogroup"
          aria-labelledby="slf-lblNotice"
          data-invalid={invalid.notice ? 'true' : 'false'}
        >
          {NOTICE.map((n) => (
            // eslint-disable-next-line jsx-a11y/label-has-associated-control
            <label className="slf-chip" key={n.value}>
              <input
                type="radio"
                name="slf-notice"
                checked={form.notice === n.value}
                onChange={() => {
                  set({ notice: n.value });
                  clearInvalid('notice');
                }}
              />
              <span className="slf-chip-face">
                <Check className="slf-tick" aria-hidden="true" />
                {n.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Locations */}
      <div className="slf-field" ref={refs.locations}>
        <span className="slf-field-label" id="slf-lblLoc">
          Preferred work location
        </span>
        <span className="slf-field-hint">Pick as many as work for you.</span>
        <div
          className="slf-chips"
          role="group"
          aria-labelledby="slf-lblLoc"
          data-invalid={invalid.locations ? 'true' : 'false'}
        >
          {locationOptions.map((loc) => (
            // eslint-disable-next-line jsx-a11y/label-has-associated-control
            <label className="slf-chip" key={loc.id}>
              <input
                type="checkbox"
                checked={form.locations.includes(loc.id)}
                onChange={() => {
                  toggleLocation(loc.id);
                  clearInvalid('locations');
                }}
              />
              <span className="slf-chip-face">
                <Check className="slf-tick" aria-hidden="true" />
                {loc.name}
              </span>
            </label>
          ))}
        </div>
        {(token || isProtected) &&
        refLoading &&
        locationOptions.length === 0 ? (
          <span className="slf-field-hint">Loading locations…</span>
        ) : null}
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className="slf-chk-row" style={{ marginTop: 10 }}>
          <input
            type="checkbox"
            className="slf-chk"
            checked={form.relocate}
            onChange={(e) => set({ relocate: e.target.checked })}
          />
          <span>I&apos;d relocate for the right role</span>
        </label>
      </div>

      <hr className="slf-rule" style={{ margin: '22px 0' }} />

      {/* Phone */}
      <div className="slf-field">
        <span className="slf-field-label">
          Best number to reach you <span className="slf-opt">— optional</span>
        </span>
        <span className="slf-field-hint">
          Leave blank and we&apos;ll use the number we contacted you on.
        </span>
        <div className="slf-input-group">
          <span className="slf-pfx" aria-hidden="true">
            +91
          </span>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="98XXXXXXXX"
            aria-label="Phone number"
            value={form.phone}
            onChange={(e) => set({ phone: e.target.value })}
          />
        </div>
      </div>

      {/* Note */}
      <div className="slf-field">
        <span className="slf-field-label">
          Anything we should know? <span className="slf-opt">— optional</span>
        </span>
        <span className="slf-field-hint">
          Timing, a counter-offer, notice buyout, visa — anything that changes
          the picture.
        </span>
        <div className="slf-input-group">
          <textarea
            rows={3}
            placeholder="Optional"
            aria-label="Anything we should know"
            value={form.note}
            onChange={(e) => set({ note: e.target.value })}
          />
        </div>
      </div>

      <p className="slf-note" style={{ margin: '20px 0 14px' }}>
        AHEDGE reviews these first. Nothing reaches {companyName} without your
        go-ahead.
      </p>

      <div className="slf-commit">
        <div className="slf-actions">
          <button
            className="slf-btn slf-btn-primary"
            type="button"
            onClick={handleSubmit}
            disabled={updateProfile.isPending}
          >
            {updateProfile.isPending ? 'Sending…' : 'Send my details'}
          </button>
          <button
            className="slf-btn slf-btn-secondary"
            type="button"
            onClick={onBack}
            disabled={updateProfile.isPending}
          >
            Back to the role
          </button>
        </div>
      </div>
    </section>
  );
}

DetailsStage.propTypes = {
  scope: PropTypes.shape({
    token: PropTypes.string,
    secMappingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  firstName: PropTypes.string,
  companyName: PropTypes.string,
  demo: PropTypes.bool,
  onSubmitted: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

DetailsStage.defaultProps = {
  scope: {},
  firstName: 'there',
  companyName: 'the company',
  demo: false,
};

export default DetailsStage;
